/** biome-ignore-all lint/correctness/noNestedComponentDefinitions: Composants personnalises du select definis localement. */

import { Box, Button, Text } from "@chakra-ui/react";
import { AsyncSelect, components, GetOptionValue, GroupBase, OptionsOrGroups } from "chakra-react-select";
import isEqual from "lodash.isequal";
import { ReactElement } from "react";

import { useIsTouchDevice } from "../../hooks";
import { PlusIcon } from "../Icons/Icons";
import { SearchSelectProperties } from "./SearchSelect";

export interface AsyncSearchSelectProperties<T>
  extends Pick<
    SearchSelectProperties<T>,
    | "alwaysCreatable"
    | "creatableFunction"
    | "getOptionLabel"
    | "isClearable"
    | "name"
    | "onBlur"
    | "onChange"
    | "placeholder"
    | "renderOption"
    | "singleValue"
    | "size"
    | "value"
  > {
  defaultOptions?: boolean | T[];
  getOptionValue?: GetOptionValue<T>;
  isLoading?: boolean;
  isSearchable?: boolean;
  isRequired?: boolean;
  loadOptions: (query: string) => Promise<OptionsOrGroups<T, GroupBase<T>>>;
  onMenuScrollToBottom?: () => void;
  isDisabled?: boolean;
}

export const AsyncSearchSelect = <T,>({
  alwaysCreatable = false,
  creatableFunction,
  defaultOptions = true,
  getOptionLabel,
  renderOption: RenderOption,
  singleValue: SingleValue,
  isRequired = false,
  ...rest
}: AsyncSearchSelectProperties<T>): ReactElement => {
  const isTouch = useIsTouchDevice();
  return (
    <AsyncSelect<T>
      chakraStyles={{
        container: (provided) => ({
          ...provided,
          width: "full",
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          bg: "transparent",
          cursor: "pointer",
          px: 2,
        }),
        group: (provided) => ({
          ...provided,
          pb: 4,
        }),
        groupHeading: (provided) => ({
          ...provided,
          color: "gray.500",
          fontSize: "md",
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          display: "none",
        }),
        menu: (provided) => ({
          ...provided,
          shadow: "lg",
          zIndex: "10",
        }),
        noOptionsMessage: (provided) => ({
          ...provided,
          p: 0,
          px: 2,
        }),
        placeholder: (provided) => ({
          ...provided,
          color: "gray.500",
        }),
      }}
      components={{
        ...(SingleValue
          ? {
              Input: (properties) => (
                <Box position="absolute">
                  <components.Input {...properties} name={rest.name} />
                </Box>
              ),
              SingleValue: (properties) => (
                <components.SingleValue {...properties}>
                  <SingleValue {...properties} />
                </components.SingleValue>
              ),
            }
          : {}),
        ...(RenderOption
          ? {
              Option: (properties) => {
                const isLast =
                  JSON.stringify(properties.options[properties.options.length - 1]) === JSON.stringify(properties.data);
                return (
                  <>
                    <Box
                      _hover={{ backgroundColor: "gray.100" }}
                      background={properties.isSelected ? "blue.500" : undefined}
                      color={properties.isSelected ? "white" : undefined}
                      cursor="pointer"
                      onClick={() => properties.selectOption(properties.data)}
                      px="3"
                      py={isTouch ? "4" : "2"}
                      w="full"
                    >
                      {RenderOption ? <RenderOption {...properties} /> : properties.children}
                    </Box>
                    {isLast && creatableFunction && alwaysCreatable && (
                      <Box pt={isTouch ? "4" : "2"} px="3">
                        <Button
                          colorScheme="info"
                          leftIcon={<PlusIcon />}
                          onClick={() => creatableFunction("")}
                          width="full"
                        >
                          Ajouter
                        </Button>
                      </Box>
                    )}
                  </>
                );
              },
            }
          : {}),
      }}
      defaultOptions={defaultOptions}
      getOptionLabel={getOptionLabel}
      isOptionSelected={(option, currentValue) =>
        currentValue[0] &&
        (getOptionLabel
          ? getOptionLabel?.(option) === getOptionLabel?.(currentValue[0])
          : isEqual(option, currentValue[0]))
      }
      isRequired={isRequired}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      noOptionsMessage={({ inputValue }) => {
        if (creatableFunction) {
          return (
            <Button
              colorScheme="info"
              leftIcon={<PlusIcon />}
              onClick={() => creatableFunction(inputValue)}
              width="full"
            >
              Ajouter {inputValue}
            </Button>
          );
        }
        return <Text>Aucun résultat</Text>;
      }}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) as any }}
      {...rest}
    />
  );
};
